import type { MakeT, MakeLogoT } from "@/app/types/makes";

/**
 * Defined order for makes/brands
 * Makes in this array will be sorted according to their position
 * Makes not in this array will be sorted alphabetically after the defined ones
 */
const MAKE_ORDER = [
  "Volkswagen Nutzfahrzeuge",
  "Audi",
  "Škoda",
  "Volkswagen",
  "Porsche",
] as const;

type MakeWithLogoT = MakeT & { make_logo: MakeLogoT | null };

/**
 * Sort function for makes/brands with priority ordering:
 * 1. Makes defined in MAKE_ORDER (sorted by their position in the array)
 * 2. All other makes alphabetically
 *
 * @param a - First make to compare
 * @param b - Second make to compare
 * @returns Comparison result for Array.sort()
 *
 * @example
 * ```tsx
 * const sortedMakes = makes.slice().sort(sortMakes);
 * ```
 */
export function sortMakes(a: MakeWithLogoT, b: MakeWithLogoT): number {
  const indexA = MAKE_ORDER.indexOf(a.name as (typeof MAKE_ORDER)[number]);
  const indexB = MAKE_ORDER.indexOf(b.name as (typeof MAKE_ORDER)[number]);

  // Both makes are in the defined order
  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }

  // Neither make is in the defined order - sort alphabetically
  if (indexA === -1 && indexB === -1) {
    return a.name.localeCompare(b.name);
  }

  // Only one make is in the defined order - prioritize it
  if (indexA === -1) return 1;
  if (indexB === -1) return -1;

  return 0;
}
